/**
 * Turning a quantity typed on the raw ledger-event form into a signed event.
 *
 * The user types a magnitude — "200 shares" — and the sign belongs to the event
 * type, not to them. The form used to send whatever `parseInt` returned for
 * every type, which meant a CANCELLATION arrived positive and died on
 * `ledger_events_shares_check`, and a TRANSFER arrived as a single positive leg
 * that the constraint happily accepted and which minted the shares it was
 * supposed to move.
 */

import { parseShareCount } from './shareCount';

/** Event types that are a single, one-sided ledger row. */
export type DirectLedgerEventType = 'ISSUANCE' | 'CANCELLATION' | 'SAFE_CONVERSION';

export type DirectLedgerEventPlan =
  | { kind: 'invalid'; error: string }
  | { kind: 'event'; shares: number };

/**
 * TRANSFER is deliberately absent from DirectLedgerEventType, and rejected at
 * runtime too: it is two legs sharing a transfer_id, written atomically by
 * `record_share_transfer` so a debit cannot exist without its credit. There is
 * no correct single-row form of it.
 */
const TRANSFER_IS_NOT_DIRECT =
  'A transfer moves shares between two holders and is recorded as a transfer, not a single event';

export function planDirectLedgerEvent(
  eventType: DirectLedgerEventType,
  quantity: string | number
): DirectLedgerEventPlan {
  if ((eventType as string) === 'TRANSFER') {
    return { kind: 'invalid', error: TRANSFER_IS_NOT_DIRECT };
  }

  const parsed = parseShareCount(quantity);
  if (parsed.kind === 'invalid') return { kind: 'invalid', error: parsed.error };

  // A magnitude, whatever sign was typed. Someone who writes "-200" for a
  // cancellation means the same as "200", and must not end up issuing shares.
  const magnitude = Math.abs(parsed.value);
  if (magnitude === 0) {
    return { kind: 'invalid', error: 'Enter a number of shares greater than zero' };
  }

  return {
    kind: 'event',
    shares: eventType === 'CANCELLATION' ? -magnitude : magnitude,
  };
}
